import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "@/components/ui/use-toast";
import { useQueryParam } from "@/hooks/use-query-param";
import { BrokerageFile, useUpdateBrokerageFile } from "@/lib/api/brokerage";
import { Loader } from "@/components/ui/loader";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";

interface UpdateBrokerageFileModalProps {
  files: BrokerageFile[];
}

export function UpdateBrokerageFileModal({ files }: UpdateBrokerageFileModalProps) {
  const [fileId, setFileId] = useQueryParam("file_id", "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const updateFile = useUpdateBrokerageFile();
  
  // Find the selected file
  const selectedFile = files.find(f => f.id === fileId);
  const isOpen = !!fileId && !!selectedFile;
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    request_date: "",
  });
  
  // Reset form when file changes
  useEffect(() => {
    if (selectedFile) {
      setFormData({
        name: selectedFile.name || "",
        request_date: selectedFile.request_date || "",
      });
      setError(null);
    }
  }, [selectedFile]);
  
  const handleClose = () => {
    setFileId("");
    setError(null);
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const result = await updateFile({
        fileId: selectedFile.id,
        name: formData.name || undefined,
        request_date: formData.request_date,
      });
      
      if (result.success) {
        toast({
          title: "Success",
          description: "File updated successfully",
        });
        handleClose();
      } else {
        setError("Failed to update file. Please try again.");
      }
    } catch (err) {
      setError("An error occurred while updating the file.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Update Brokerage File</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Original Filename</Label>
              <p className="text-sm text-muted-foreground">
                {selectedFile?.Document?.filename || "No filename"}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Custom Name (Optional)</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter custom name..."
                disabled={isSubmitting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="request_date">Request Date</Label>
              <Input
                id="request_date"
                type="date"
                value={moment.tz(formData.request_date,CommonDestinationTimezone).format("YYYY-MM-DD")}
                onChange={(e) => setFormData({ ...formData, request_date: e.target.value })}
                required
                disabled={isSubmitting}
                max={moment.tz(CommonDestinationTimezone).format("YYYY-MM-DD")}
              />
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader svgClassName="w-4 h-4 mr-2" />
                  Updating...
                </>
              ) : (
                "Update File"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
